import { Clock } from 'lucide-react';
import { fmt, fmtPct } from '../../utils/formatting';

// ── Helpers ────────────────────────────────────────────────────
export const netInvested = (f) => (f.investedAmount || 0) - (f.redeemed || 0);

// Format cache age for badges
export const fmtAge = (hours) => {
  if (hours == null) return null;
  if (hours < 1) return `${Math.round(hours * 60)} min fa`;
  if (hours < 24) return `${Math.round(hours)}h fa`;
  if (hours < 48) return 'ieri';
  return `${Math.round(hours / 24)}g fa`;
};

// Age badge component for Risk/MC sections
export const AgeBadge = ({ hours, isDark }) => {
  if (hours == null) return null;
  const isStale = hours > 24;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${isStale
      ? (isDark ? 'bg-amber-900/30 text-amber-400 border border-amber-800/40' : 'bg-amber-100 text-amber-700 border border-amber-300')
      : 'bg-elevated text-txt-muted'}`}>
      <Clock size={10} />
      {isStale ? `Dati non aggiornati — ${fmtAge(hours)}` : `Aggiornato ${fmtAge(hours)}`}
    </span>
  );
};

// ── Recovery Badge ─────────────────────────────────────────────
export const RECOVERY_STYLES_DARK = {
  breve:    { label: 'Rec. Breve',  icon: '🟢', bg: 'bg-emerald-900/30 border-emerald-700/40 text-emerald-300' },
  medio:    { label: 'Rec. Medio',  icon: '🟡', bg: 'bg-amber-900/30 border-amber-700/40 text-amber-300' },
  lungo:    { label: 'Rec. Lungo',  icon: '🔴', bg: 'bg-red-900/30 border-red-700/40 text-red-300' },
  positivo: { label: 'In guadagno', icon: '✅', bg: 'bg-emerald-900/30 border-emerald-700/40 text-emerald-300' },
};
export const RECOVERY_STYLES_LIGHT = {
  breve:    { label: 'Rec. Breve',  icon: '🟢', bg: 'bg-emerald-100 border-emerald-300 text-emerald-700' },
  medio:    { label: 'Rec. Medio',  icon: '🟡', bg: 'bg-amber-100 border-amber-300 text-amber-700' },
  lungo:    { label: 'Rec. Lungo',  icon: '🔴', bg: 'bg-red-100 border-red-300 text-red-700' },
  positivo: { label: 'In guadagno', icon: '✅', bg: 'bg-emerald-100 border-emerald-300 text-emerald-700' },
};

export const RecoveryBadge = ({ isin, recoveryData, isDark = true }) => {
  if (!recoveryData || !isin) return null;
  const data = recoveryData[isin];
  if (!data) return null;
  const styles = isDark ? RECOVERY_STYLES_DARK : RECOVERY_STYLES_LIGHT;
  const style = styles[data.horizon] || styles.lungo;
  return (
    <div className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${style.bg}`} title={data.note || ''}>
      <span>{style.icon}</span>
      <span>{style.label}</span>
      {data.confidence && <span className="opacity-60">({data.confidence})</span>}
    </div>
  );
};

// ── Summary Card ───────────────────────────────────────────────
export const SummaryCard = ({ icon: Icon, label, value, sub, subNeutral, color = 'blue' }) => (
  <div className="bg-card rounded-lg p-3">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} className={`text-${color}-400`} />
      <span className="text-xs text-txt-muted">{label}</span>
    </div>
    <div className="text-lg font-semibold">{value}</div>
    {sub && <div className={`text-xs ${typeof sub === 'string' && sub.startsWith('-') ? 'text-red-400' : 'text-emerald-400'}`}>{sub}</div>}
    {subNeutral && <div className="text-xs text-txt-muted">{subNeutral}</div>}
  </div>
);

// ── Pie chart colors ────────────────────────────────────────
export const PIE_COLORS = {
  fundiMedio: '#3b82f6',  // blue-500
  fundiLungo: '#a855f7',  // purple-500
  azioni: '#10b981',      // emerald-500
};

// ── Custom tooltip for pie chart ────────────────────────────
export const PieTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0];
  return (
    <div className="bg-elevated border border-brd-subtle rounded-lg px-3 py-2 text-xs shadow-lg">
      <div className="font-medium text-txt-primary">{d.name}</div>
      <div className="text-txt-primary">€{fmt(d.value)}</div>
      <div className="text-txt-secondary">{d.payload.pct}%</div>
    </div>
  );
};

// ── Custom legend for pie chart ─────────────────────────────
export const PieLegendItem = ({ color, label, value, pct, pnl, pnlPct }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
    <span className="text-txt-secondary min-w-[100px]">{label}</span>
    <span className="font-medium text-txt-primary">€{fmt(value)}</span>
    <span className="text-txt-muted">{pct}%</span>
    {pnl != null && (
      <span className={pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
        {pnl >= 0 ? '+' : ''}{fmt(pnl)} ({fmtPct(pnlPct)})
      </span>
    )}
  </div>
);

// Colors for per-fund lines (Monte Carlo)
export const fundLineColors = ['#a855f7', '#3b82f6', '#f59e0b', '#16a34a', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];
